import { AxiosInstance } from 'axios';
import { FacebookAccount } from '../facebook/types';
import { DirectInstagramAccount } from '../instagram/types';

export const SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE = 'FACEBOOK_PAGE' as const;
export const SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT = 'INSTAGRAM_ACCOUNT' as const;
export const SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT = 'DIRECT_INSTAGRAM_ACCOUNT' as const;
export type SocialMediaAssetType =
  | typeof SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE
  | typeof SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT
  | typeof SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT;

export type AccountStatus = 'ACTIVE' | 'INACTIVE' | 'ERROR';

export type FacebookAccountDto = {
  id: string | null;
  name: string | null;
  facebookPages: FacebookPageDto[];
  instagramAccounts: InstagramAccountDto[];
  accessTokenExpiration: string | null;
  isDisconnected: boolean;
};

export type FacebookPageDto = {
  id: string;
  name: string;
  status: AccountStatus;
  error: string | null;
  type: typeof SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE;
};

export type InstagramAccountDto = {
  id: string;
  username: string;
  name: string;
  parentFacebookPageId: string | null;
  status: AccountStatus;
  error: string | null;
  type: typeof SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT;
};

export const notConnectedFacebookAccount: FacebookAccount = {
  id: null,
  name: null,
  facebookPages: [],
  instagramAccounts: [],
  accessTokenExpiration: null,
  isDisconnected: true,
};

export type DirectInstagramAccountDto = {
  id: string | null;
  name: string | null;
  username: string | null;
  accessTokenExpiration: string | null;
  isDisconnected: boolean;
};

export const notConnectedInstagramAccount: DirectInstagramAccount = {
  id: null,
  type: SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT,
  name: null,
  username: null,
  accessTokenExpiration: null,
  isDisconnected: true,
};

const toInstagramDomain = (dto: DirectInstagramAccountDto): DirectInstagramAccount => {
  return {
    id: dto.id,
    type: SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT,
    name: dto.name,
    username: dto.username,
    isDisconnected: dto.isDisconnected,
    accessTokenExpiration: dto.accessTokenExpiration ? new Date(dto.accessTokenExpiration) : null,
  };
};

export type SocialPostingApi = ReturnType<typeof getSocialPostingApi>;

const toDomain = (dto: FacebookAccountDto): FacebookAccount => {
  return {
    id: dto.id,
    name: dto.name,
    isDisconnected: dto.isDisconnected,
    accessTokenExpiration: dto.accessTokenExpiration ? new Date(dto.accessTokenExpiration) : null,
    facebookPages: dto.facebookPages.map((x) => ({ ...x, type: SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE })),
    instagramAccounts: dto.instagramAccounts.map((x) => ({
      ...x,
      type: SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT,
    })),
  };
};

export const getSocialPostingApi = (axiosInstance: AxiosInstance) => {
  const getFacebookAccount = async (): Promise<FacebookAccount> =>
    axiosInstance
      .get<FacebookAccountDto>('/social/accounts/facebook')
      .then((r) => toDomain(r.data));

  const authenticateFacebook = async (accessToken: string): Promise<FacebookAccount> =>
    axiosInstance
      .post<FacebookAccountDto>('/social/accounts/facebook/auth', {
        accessToken,
      })
      .then((r) => toDomain(r.data));

  const disconnectFacebook = async (): Promise<void> =>
    axiosInstance.delete('/social/accounts/facebook').then(() => {});

  const getInstagramAccount = async (): Promise<DirectInstagramAccount> =>
    axiosInstance
      .get<DirectInstagramAccountDto>('/social/accounts/instagram')
      .then((r) => toInstagramDomain(r.data));

  const authenticateInstagram = async (
    code: string,
    redirectUrl: string
  ): Promise<DirectInstagramAccount> =>
    axiosInstance
      .post<DirectInstagramAccountDto>('/social/accounts/instagram/auth', {
        code,
        redirectUrl,
      })
      .then((r) => toInstagramDomain(r.data));

  const disconnectInstagram = async (): Promise<void> =>
    axiosInstance.delete('/social/accounts/instagram').then(() => {});

  return {
    getFacebookAccount,
    authenticateFacebook,
    disconnectFacebook,
    getInstagramAccount,
    authenticateInstagram,
    disconnectInstagram,
  };
};
